import { c as _c } from "react/compiler-runtime";
import { useOpenCv } from 'opencv-react-ts';
import { useEffect } from 'react';
import { getLogger } from '@/idv/utils';
import { isOpenCvUsable } from '../components/camera/utils/graphics-utils/graphics-processing-utils';
const {
  logInfo,
  logWarn
} = getLogger({
  location: 'id-doc'
});
const UNUSABLE_LOG_DELAY_MS = 30_000;

// Module scope, so a page reports this once however often the document step is re-entered.
let unusableLoggedAt: number | undefined;
let recoveryLogged = false;

/**
 * `missing`: the script is still downloading or evaluating. `uninitialized`: evaluated, wasm still
 * instantiating. `usable` while the provider's own `cv` is not: opencv-react-ts never handed it to
 * this tree.
 */
const getGlobalCvState = (): 'usable' | 'uninitialized' | 'missing' => {
  const globalCv: unknown = window.cv;
  if (isOpenCvUsable(globalCv)) {
    return 'usable';
  }
  return globalCv === undefined ? 'missing' : 'uninitialized';
};

/** A null render: it exists to run these effects inside opencv-react-ts's provider. */
const OpenCvGuard = () => {
  const $ = _c(8);
  const {
    cv
  } = useOpenCv();
  let t0;
  if ($[0] !== cv) {
    t0 = isOpenCvUsable(cv);
    $[0] = cv;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const usable = t0;
  let t1;
  let t2;
  if ($[2] !== usable) {
    t1 = () => {
      if (!usable) {
        return;
      }
      window.Module = undefined;
      if (unusableLoggedAt !== undefined && !recoveryLogged) {
        recoveryLogged = true;
        logInfo("OpenCV became usable after the slow-load warning", {
          elapsedMs: Date.now() - unusableLoggedAt
        });
      }
    };
    t2 = [usable];
    $[2] = usable;
    $[3] = t1;
    $[4] = t2;
  } else {
    t1 = $[3];
    t2 = $[4];
  }
  useEffect(t1, t2);
  let t3;
  let t4;
  if ($[5] !== usable) {
    t3 = () => {
      if (usable || unusableLoggedAt !== undefined) {
        return;
      }
      const timer = setTimeout(_temp, UNUSABLE_LOG_DELAY_MS);
      return () => clearTimeout(timer);
    };
    t4 = [usable];
    $[5] = usable;
    $[6] = t3;
    $[7] = t4;
  } else {
    t3 = $[6];
    t4 = $[7];
  }
  useEffect(t3, t4);
  return null;
};
declare global {
  interface Window {
    Module?: unknown;
  }
}
export { OpenCvGuard };
function _temp() {
  unusableLoggedAt = Date.now();
  logWarn(`OpenCV not usable ${UNUSABLE_LOG_DELAY_MS}ms after mount, autocapture falls back to the manual shutter`, undefined, {
    globalCv: getGlobalCvState()
  });
}